import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { FileManagerMountsState } from '../logic/core/useFileManagerMounts';
import type { FileSystemItem } from '../types';

// Takes the mounts state explicitly rather than self-injecting via useFileManagerMounts():
// this mutation is used from provideFileManagerUpload()/provideFileManagerCreateFolder(), both called
// directly in FileManager.vue's own setup() — the same instance that calls
// provideFileManagerMounts(), which a component can never inject its own provide() call from.
export const useCreateDirectoryMutation = (
  mounts: Pick<FileManagerMountsState, 'resolve'>,
) => {
  const queryClient = useQueryClient();
  const { resolve } = mounts;

  return useMutation({
    mutationKey: ['directoryService', 'createFolder'],
    mutationFn: ({
      parentPath,
      name,
    }: {
      parentPath: string;
      name: string;
    }): Promise<FileSystemItem> => {
      // parentPath is virtual; the drive it lives on is resolved here, at the single point that
      // actually needs a driveId. Callers are already gated on canCreateFolderAt(), so an
      // unresolvable path means a bug rather than a user action — hence a throw, not a silent no-op.
      const target = resolve(parentPath);
      if (!target) {
        throw new Error(`Cannot create a folder in ${parentPath}: no accessible library`);
      }
      return sharePointDriveService.createFolder(target, name);
    },
    // Invalidation stays in virtual paths — the whole file manager caches by them.
    onSuccess: (_data, { parentPath }) =>
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(parentPath),
      }),
  });
};
